// OcrCapture — file picker with camera capture hint, runs OCR via kyc proc,
// surfaces the parsed result + "Use these values" CTA.
//
// Mobile (Android Chrome) honours `capture="environment"` to launch the rear
// camera. Desktop falls back to a regular file picker.

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

type ScanKind = 'aadhaar' | 'pan' | 'cheque';

interface AadhaarResult {
  kind: 'aadhaar';
  name_en: string;
  name_ta: string;
  dob: string;
  gender: 'M' | 'F' | 'X';
  aadhaar_last4: string;
  address_line: string;
  district: string;
  state: string;
  pincode: string;
  confidence: number;
  dry_run?: boolean;
}

interface PanResult {
  kind: 'pan';
  pan: string;
  name_en: string;
  dob: string;
  confidence: number;
  dry_run?: boolean;
}

interface ChequeResult {
  kind: 'cheque';
  account_number: string;
  ifsc: string;
  account_holder: string;
  bank_name: string;
  branch: string;
  confidence: number;
  dry_run?: boolean;
}

export type OcrResult = AadhaarResult | PanResult | ChequeResult;

interface Props {
  kind: ScanKind;
  label: string;
  on_result: (r: OcrResult) => void;
}

async function read_as_base64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result;
      if (typeof data !== 'string') {
        reject(new Error('unexpected reader result'));
        return;
      }
      // Strip the `data:image/...;base64,` prefix — server expects raw b64
      const comma = data.indexOf(',');
      resolve(comma >= 0 ? data.slice(comma + 1) : data);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

export function OcrCapture({ kind, label, on_result }: Props): ReactElement {
  const { t } = useTranslation();
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  const on_file = async (file: File): Promise<void> => {
    set_busy(true);
    set_error(null);
    try {
      const image_base64 = await read_as_base64(file);
      let result: OcrResult;
      if (kind === 'aadhaar') {
        const r = await orpc.kyc.scan_aadhaar({ image_base64 });
        result = { ...r, kind: 'aadhaar' };
      } else if (kind === 'pan') {
        const r = await orpc.kyc.scan_pan({ image_base64 });
        result = { ...r, kind: 'pan' };
      } else {
        const r = await orpc.kyc.scan_cheque({ image_base64 });
        result = { ...r, kind: 'cheque' };
      }
      on_result(result);
    } catch (e) {
      set_error(friendly_error(e));
    } finally {
      set_busy(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <label
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-line bg-bg-elev hover:bg-surface cursor-pointer ${
          busy ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        📷 {busy ? '…' : label}
        <input
          type="file"
          accept="image/*"
          // eslint-disable-next-line react/no-unknown-property — `capture` is a valid HTML attr, React types lag
          {...({ capture: 'environment' } as Record<string, string>)}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void on_file(f);
            e.target.value = '';
          }}
        />
      </label>
      {error && <span className="text-[11px] text-bad">{error}</span>}
      {busy && <span className="text-[11px] text-ink/60">{t('ocr.scanning', 'Scanning…')}</span>}
    </div>
  );
}
